import { ArrowLeftOutlined, DesktopOutlined, MobileOutlined } from '@ant-design/icons';
import { Alert, Button, Segmented, Space, Spin, Typography, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { BlogPostStatus, getBlogPost, listBlogCategories, listBlogPosts } from '../api/blog';
import type { BlogCategory, BlogPostSummary } from '../api/blog';
import { ApiError } from '../api/errors';
import { AppRoutes } from '../routes';
import { BlogArticlePreview } from './blog/preview/BlogArticlePreview';
import { BlogListPreview } from './blog/preview/BlogListPreview';
import { WebsiteFrame } from './blog/preview/WebsiteFrame';
import type { PreviewDevice } from './blog/preview/WebsiteFrame';
import { fromPost, fromSummary } from './blog/preview/previewModel';
import type { PreviewArticle, PreviewCategory, PreviewPost } from './blog/preview/previewModel';

/** Nguồn bài cho bản xem trước: đúng như khách thấy, hoặc mọi bài kể cả nháp. */
type PreviewSource = 'public' | 'all';

/** Số bài mỗi trang trong bản xem trước. */
const postsPerPage = 6;

/** Số bài trong khung "Bài viết mới nhất". */
const recentCount = 5;

/** Đủ lớn để lấy hết bài của một blog doanh nghiệp trong một lần gọi. */
const maximumPosts = 200;

const sourceOptions: { value: PreviewSource; label: string }[] = [
  { value: 'public', label: 'Như khách xem' },
  { value: 'all', label: 'Tất cả bài (cả nháp)' },
];

const deviceOptions: { value: PreviewDevice; label: string; icon: ReactNode }[] = [
  { value: 'desktop', label: 'Máy tính', icon: <DesktopOutlined /> },
  { value: 'mobile', label: 'Điện thoại', icon: <MobileOutlined /> },
];

/** Khách chỉ thấy bài đã đăng và đã tới ngày đăng. */
function isVisibleToVisitors(post: BlogPostSummary): boolean {
  return post.status === BlogPostStatus.Published && !post.isScheduled;
}

/** Địa chỉ hiện trên thanh địa chỉ của khung xem trước — đúng dạng địa chỉ website sẽ dùng. */
function previewUrl(article: PreviewArticle | null, category: string | null, page: number): string {
  if (article !== null) {
    return `https://mvalab.vn/blog/${article.slug}`;
  }

  const query = new URLSearchParams();

  if (category !== null) {
    query.set('category', category);
  }

  if (page > 1) {
    query.set('page', page.toString());
  }

  const text = query.toString();

  return `https://mvalab.vn/blog${text === '' ? '' : `?${text}`}`;
}

/**
 * Trang "Xem như trên website" — dựng lại trang blog của mvalab.vn theo bố cục kiểu RTC bằng chính dữ liệu
 * trong ERP, để người viết thấy bài hiện ra thế nào trước khi website thật làm xong.
 *
 * Chỉ đọc: không có thao tác nào ở đây ghi gì vào hệ thống.
 */
export function BlogPreviewPage(): ReactNode {
  const navigate = useNavigate();
  const frameTop = useRef<HTMLDivElement>(null);

  const [source, setSource] = useState<PreviewSource>('public');
  const [device, setDevice] = useState<PreviewDevice>('desktop');

  const [summaries, setSummaries] = useState<readonly BlogPostSummary[] | null>(null);
  const [categories, setCategories] = useState<readonly BlogCategory[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [article, setArticle] = useState<PreviewArticle | null>(null);
  const [isOpening, setIsOpening] = useState(false);

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      try {
        const [posts, loadedCategories] = await Promise.all([
          listBlogPosts({ pageNumber: 1, pageSize: maximumPosts }),
          listBlogCategories(),
        ]);

        if (isCurrent) {
          setSummaries(posts.items);
          setCategories(loadedCategories);
        }
      } catch (error) {
        if (isCurrent) {
          setErrorMessage(error instanceof ApiError ? error.message : 'Không tải được bài viết.');
        }
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, []);

  const posts = useMemo<readonly PreviewPost[]>(
    () =>
      (summaries ?? [])
        .filter((post) => source === 'all' || isVisibleToVisitors(post))
        .map((post) => fromSummary(post, categories)),
    [summaries, categories, source],
  );

  const previewCategories = useMemo<readonly PreviewCategory[]>(
    () =>
      categories
        .map((category) => ({
          name: category.name,
          slug: category.slug,
          count: posts.filter((post) => post.categorySlug === category.slug).length,
        }))
        .filter((category) => category.count > 0),
    [categories, posts],
  );

  const filtered = activeCategory === null ? posts : posts.filter((post) => post.categorySlug === activeCategory);
  const totalPages = Math.max(1, Math.ceil(filtered.length / postsPerPage));
  const pagePosts = filtered.slice((page - 1) * postsPerPage, page * postsPerPage);
  const recent = posts.slice(0, recentCount);

  const related =
    article === null
      ? []
      : posts
          .filter((post) => post.id !== article.id && post.categorySlug !== null && post.categorySlug === article.categorySlug)
          .slice(0, 3);

  function scrollToFrame(): void {
    frameTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function selectCategory(slug: string | null): void {
    setActiveCategory(slug);
    setPage(1);
    setArticle(null);
    scrollToFrame();
  }

  async function open(post: PreviewPost): Promise<void> {
    setIsOpening(true);

    try {
      setArticle(fromPost(await getBlogPost(post.id), categories));
      scrollToFrame();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không mở được bài viết.');
    } finally {
      setIsOpening(false);
    }
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Space size={12} align="center">
          <Button icon={<ArrowLeftOutlined />} aria-label="Về trang Blog" onClick={() => { void navigate(AppRoutes.Blog); }} />
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Xem như trên website
            </Typography.Title>
            <Typography.Text type="secondary">
              Bản mô phỏng trang blog của mvalab.vn với dữ liệu thật trong ERP — chỉ để xem, không ghi gì.
            </Typography.Text>
          </div>
        </Space>
        <Space wrap>
          <Segmented<PreviewSource>
            options={sourceOptions}
            value={source}
            onChange={(value) => {
              setSource(value);
              setPage(1);
              setArticle(null);
            }}
          />
          <Segmented<PreviewDevice> options={deviceOptions} value={device} onChange={setDevice} />
        </Space>
      </div>

      {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

      <div ref={frameTop} />

      <Spin spinning={summaries === null || isOpening}>
        <WebsiteFrame url={previewUrl(article, activeCategory, page)} device={device}>
          {article === null ? (
            <BlogListPreview
              posts={pagePosts}
              page={page}
              totalPages={totalPages}
              onPage={(number) => {
                setPage(number);
                scrollToFrame();
              }}
              totalCount={posts.length}
              categories={previewCategories}
              recent={recent}
              activeCategory={activeCategory}
              onSelectCategory={selectCategory}
              onOpen={(post) => { void open(post); }}
            />
          ) : (
            <BlogArticlePreview
              article={article}
              totalCount={posts.length}
              categories={previewCategories}
              recent={recent}
              related={related}
              onBack={() => {
                setArticle(null);
                scrollToFrame();
              }}
              onSelectCategory={selectCategory}
              onOpen={(post) => { void open(post); }}
            />
          )}
        </WebsiteFrame>
      </Spin>
    </Space>
  );
}
